<script lang="ts">
  import { PUBLIC_DO_TILES_URL } from '$env/static/public';

  import { onDestroy, onMount } from 'svelte';

  import { fade } from 'svelte/transition';

  import { ArtAssetType, Feature } from '@do/models';
  import { Previewer, Viewer, Viewport } from '@do/viewer';

  import { scrollToTopAsync } from '@shared/libs/tools';
  import { useFlag } from '@shared/libs/unleash';
  import type { DataItem, OriginalSizeSingle } from '@shared/types';
  import { theme, ThemeModes } from '@shared/ui/theme';
  import { cn, wait } from '@shared/utils';

  import type { ArtAsset } from '@entities/art-asset';

  import AccordionDetails from './accordion-details.svelte';
  import BtnFullsize from './btn-fullsize.svelte';
  import BtnMaxdetails from './btn-maxdetails.svelte';
  import BtnUnScale from './btn-unscale.svelte';
  import ZoomControls from './zoom-controls.svelte';

  let viewerContainer: HTMLDivElement;
  let previewerContainer: HTMLDivElement;

  let viewer: Viewer;
  let previewer: Previewer;

  let percentage = 0;
  let percentageRaw = 0;
  let percentageActual = 0;
  let minVal = 0;
  let maxVal = 100;

  export let onClose: () => void;
  export let placeholderElement: HTMLImageElement | undefined = undefined;
  export let digitalOriginalData: ArtAsset['digital_original'];
  export let mainPhotoId: string | null = null;
  export let artists: ArtAsset['artists'];
  export let description: string;
  export let year: string;
  export let originalSizeX: OriginalSizeSingle;
  export let originalSizeY: OriginalSizeSingle;
  export let originalUnits: string | null;
  // export let styles: DataItem[] | null = null;
  export let medium: DataItem[] | null = null;
  export let artAssetType: ArtAssetType | null = null;

  const viewerShadowsFlag = useFlag(Feature.UfdaViewerShadows);
  const viewerHighlightFlag = useFlag(Feature.UfdaViewerHightlight);
  const viewerUseDprFlag = useFlag(Feature.UfdaViewerUseDpr);

  const digitalOriginalId = digitalOriginalData?.file_id || mainPhotoId;

  const DZI_URL = `/api/digital-original/${digitalOriginalId}/${digitalOriginalId}.dzi`; // get dzi via proxy
  const TILES_URL = `${PUBLIC_DO_TILES_URL}/${digitalOriginalId}`;

  const moveDistance = 200;

  onMount(async () => {
    // if its mobile iOS device, overflow property can be ignored so we have to scrollToTop and use position fixed instead
    if (window.matchMedia('(hover: none), (hover: on-demand), (-moz-touch-enabled: 1), (pointer:coarse)').matches) {
      await scrollToTopAsync();
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'hidden';
    }

    const drawShadows = $viewerShadowsFlag;
    const drawHighlight = $viewerHighlightFlag;

    viewer = new Viewer(
      TILES_URL,
      DZI_URL,
      placeholderElement,
      drawShadows,
      drawHighlight,
      $theme === ThemeModes.Dark,
      $viewerUseDprFlag,
    );
    previewer = new Previewer();
    const viewerEL = viewer.getViewer();
    const previewerEl = previewer.getPreviewer();
    viewerContainer.appendChild(viewerEL);
    previewerContainer.appendChild(previewerEl);
    viewer.setOnViewportChangeCallback((viewport: Viewport | null) => {
      requestAnimationFrame(() => {
        if (!viewport) return;
        percentageRaw = viewport.zoomPercentageTarget; // get 'target' value to avoid input range unnecessary and strange animation
        percentage = Math.round(percentageRaw);
        percentageActual = Math.round(viewport.zoomPercentage); // get current percentage value (changes during animation)
        minVal = Math.round(viewport.zoomPercentageMin); // get slider calculated min val if onResize called
        maxVal = Math.round(viewport.zoomPercentageMax); // get slider calculated max val
      });
    });

    viewer.setOnInitCallback(async () => {
      // start anim actions after viewer initialized
      //timeout before initial anim begin (just for anim purpose, you can run it immediately)
      await wait(500);

      // wait for viewer open animation complete
      await viewer.animateOnOpen();

      // popoverTimer = setTimeout(() => {
      //   popoverBtn?.click();
      //   isSummaryOpen = true;
      // }, transitionDelay * 3); // open popover after animations done

      isControlsDelayed = false;
      isControlsVisible = true;
      previewer.tilesUrl = TILES_URL;
      const viewport = viewer?.getViewport?.();
      if (viewport) previewer.init(viewport);
    });
  });

  function handleScaleInitial() {
    viewer.setZoomToInitial?.();
  }
  function handleZoomIn() {
    viewer.zoomInAnimated?.();
  }
  function handleZoomOut() {
    viewer.zoomOutAnimated?.();
  }
  function handleChangeZoomValue(value: number) {
    viewer.setZoomLevelAnimated?.(value, false);
  }
  function handleZoomMax() {
    viewer.setZoomToMax?.();
  }

  async function handleOnClose() {
    await viewer.animateOnClose();
    onClose?.();
  }

  function handleKeyDown(e: KeyboardEvent) {
    // e.preventDefault();
    showControls();
    if (e.code === 'Escape') handleOnClose?.();
    if (e.code === 'Equal') handleZoomIn();
    if (e.code === 'Minus') handleZoomOut();
    if (viewer.movePositionAnimated) {
      if (e.code === 'ArrowLeft') viewer.movePositionAnimated(moveDistance, 0);
      if (e.code === 'ArrowRight') viewer.movePositionAnimated(-moveDistance, 0);
      if (e.code === 'ArrowUp') viewer.movePositionAnimated(0, moveDistance);
      if (e.code === 'ArrowDown') viewer.movePositionAnimated(0, -moveDistance);
    }
  }

  let isControlsVisible = true;
  let isControlsHovered = false;
  let isControlsDelayed = true;

  let isSummaryOpen = false;

  //TODO - improve logic for show/hide controls when user active/inactive
  const time = 2; // time in seconds before controls hide
  const initialTime = 4; // time in seconds before controls hide first time
  let countdown = initialTime;

  // let popoverBtn: HTMLElement;
  // let popoverTimer: ReturnType<typeof setTimeout>;

  const showControls = () => {
    if (isControlsDelayed) return;
    isControlsVisible = true;
    countdown = time; // show controls & add time to timer before controls hide
  };

  setInterval(() => {
    if (!isControlsHovered && !isControlsDelayed) {
      // do nothing while we hover controls
      countdown > 0 ? (countdown = countdown - 1) : (isControlsVisible = false);
      // countdown > 0 ? (countdown = countdown - 1) : (isControlsVisible = true);
    }
  }, 1000);

  const onControlsHoverOn = () => {
    isControlsHovered = true;
  };
  const onControlsHoverOff = () => {
    isControlsHovered = false;
  };

  onDestroy(() => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';

    // clearTimeout(popoverTimer);
  });

  const transitionDuration = 250;
  const transitionDelay = 350;

  // hide zoom controls if image too small to zoom
  $: isZoomable = minVal !== maxVal;
</script>

<!-- move listeners to document so actions will be always focused when viewer open -->
<svelte:document
  on:keydown="{handleKeyDown}"
  on:wheel="{showControls}"
  on:mousemove="{showControls}"
  on:touchmove="{showControls}"
/>

<div
  class="viewer dark:bg-dark-400 group fixed left-0 top-0 z-40 h-full w-full cursor-grab select-none bg-[#C5C5C5] outline-none active:cursor-grabbing"
  bind:this="{viewerContainer}"
  tabindex="-1"
  role="button"
  on:wheel="{(e) => e.preventDefault()}"
  in:fade="{{ duration: transitionDuration, delay: transitionDelay }}"
  out:fade="{{ duration: transitionDuration, delay: transitionDelay }}"
>
  <!-- on:mousemove="{(e) => e.preventDefault()}" preventDefault on container occurs on drag events for input range -->

  <!-- controls area -->
  <div
    class="{cn('pointer-events-none absolute left-0 top-0 h-full w-full opacity-0 duration-300', {
      'opacity-100': isControlsVisible,
    })}"
    on:mouseenter="{onControlsHoverOn}"
    on:mouseleave="{onControlsHoverOff}"
    role="button"
    tabindex="0"
    in:fade="{{ duration: transitionDuration, delay: transitionDelay * 2 }}"
    out:fade="{{ duration: transitionDuration, delay: 0 }}"
  >
    <!-- desktop controls start -->
    <div
      class="
    min-square-md:flex pointer-events-none absolute right-0 top-0 hidden h-full flex-col justify-between p-5 text-right"
    >
      <div class="pointer-events-auto">
        <!-- area for close btn -->
        <BtnUnScale on:click="{handleOnClose}" />
      </div>
      <div
        class="{cn('flex flex-col items-end gap-2.5', {
          hidden: !isZoomable,
        })}"
      >
        <!-- area for slider and + - btns and same -->
        <div class="h-[275px]">
          <ZoomControls
            min="{minVal}"
            max="{maxVal}"
            values="{[percentage]}"
            on:stop="{(e) => handleChangeZoomValue(e.detail.value)}"
            on:zoomIn="{handleZoomIn}"
            on:zoomOut="{handleZoomOut}"
            vertical
          />
        </div>
        <div class="pointer-events-auto">
          <BtnFullsize isMinimized on:click="{handleScaleInitial}" />
        </div>
        <div class="pointer-events-auto">
          <BtnMaxdetails isMinimized on:click="{handleZoomMax}" />
        </div>
      </div>
      <div
        class="{cn('flex min-h-[100px] items-end justify-end', {
          hidden: !isZoomable,
        })}"
      >
        <!-- area for previewer -->
        <div
          class="dark:bg-dark-500/60 pointer-events-auto flex h-fit flex-col items-center rounded-tl-lg rounded-tr-lg bg-white/70 drop-shadow backdrop-blur-2xl"
        >
          <div class="text-xxs text-default-900 dark:text-dark-900 font-medium">Zoom: {percentageActual}%</div>
          <div class="flex min-h-10 w-20 items-center justify-center" bind:this="{previewerContainer}"></div>
        </div>
      </div>
    </div>
    <!-- desktop controls end -->

    <!-- mobile controls start -->
    <div
      class="min-square-md:hidden pointer-events-none absolute bottom-0 left-0 z-40 flex h-full w-full flex-row justify-between p-5 text-right duration-300"
    >
      <div class="flex w-full flex-row items-end gap-2">
        <!-- area for slider and + - btns and same -->
        <div
          class="{cn('pointer-events-auto', {
            hidden: !isZoomable,
          })}"
        >
          <BtnMaxdetails isMinimized size="sm" on:click="{handleZoomMax}" />
        </div>
        <div
          class="{cn('pointer-events-auto', {
            hidden: !isZoomable,
          })}"
        >
          <BtnFullsize isMinimized size="sm" on:click="{handleScaleInitial}" />
        </div>
        <div class="grow">
          <div
            class="{cn('flex w-full max-w-72 flex-grow flex-col items-center gap-2.5', {
              hidden: !isZoomable,
            })}"
          >
            <div
              class="text-default-900 dark:bg-dark-500/60 dark:text-dark-900 rounded-[4px] bg-white/85 px-2 py-1 text-xs font-medium"
            >
              Zoom: {percentageActual}%
            </div>
            <ZoomControls
              min="{minVal}"
              max="{maxVal}"
              values="{[percentage]}"
              on:stop="{(e) => handleChangeZoomValue(e.detail.value)}"
              on:zoomIn="{handleZoomIn}"
              on:zoomOut="{handleZoomOut}"
              size="sm"
            />
          </div>
        </div>
        <div class="pointer-events-auto">
          <!-- area for close btn -->
          <BtnUnScale size="sm" on:click="{handleOnClose}" />
        </div>
      </div>
    </div>
    <!-- mobile controls end -->

    <!-- summary/description area -->
    <div
      class="min-square-md:bottom-5 min-square-md:top-[unset] pointer-events-none absolute left-auto right-5 top-5 flex w-full max-w-[calc(100%-40px)] justify-center gap-2 transition-opacity duration-300"
    >
      <div class="pointer-events-auto">
        <AccordionDetails
          {artAssetType}
          {digitalOriginalData}
          {artists}
          {description}
          {year}
          {originalSizeX}
          {originalSizeY}
          {originalUnits}
          {medium}
          bind:isOpen="{isSummaryOpen}"
          on:moreDetails="{handleOnClose}"
        />
      </div>
    </div>
  </div>
</div>
